import { type CouponCodeSignupFormData } from '@/features/cms/components/CMS/types'
import { Body } from '@shipt/design-system-typography'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { routes } from '@shared/constants/routes'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { useUtmUrl } from '@/features/shared/hooks/useUtmUrl'
import {
  CMSFormInputWrapper,
  CMSFormSubmitButton,
  CMSInputWrapper,
} from '@/features/cms/components/CMS/components/Forms/styles'
import { couponCodeQueryOptions } from '@/features/authentication/services/SignupPromotions/queries'
import { useForm } from 'react-hook-form'
import { getErrorMessage } from '@/features/shared/utils/dataFetching/utils'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { useQueryClient } from '@tanstack/react-query'
import { TextInput } from '@shipt/design-system-inputs'

type CouponForm = { coupon: string }

/**
 * This component corresponds to the `coupon_code_signup_form` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/coupon_code_signup_form
 * - **Production** - https://cms.shipt.com/content-type/pool/coupon_code_signup_form
 */
export const CouponCodeSignupForm = ({
  content_type_id,
  id,
  data: { disclaimer },
}: CouponCodeSignupFormData) => {
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const router = useSegwayRouter()
  const searchParams = useSearchQueryParams()
  const signupUrl = useUtmUrl(routes.SIGN_UP.url)
  const queryClient = useQueryClient()
  const defaultPromoCode = searchParams.promocode?.toString() || ''

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm<CouponForm>({
    defaultValues: { coupon: defaultPromoCode },
  })

  const onSubmit = async ({ coupon }: CouponForm) => {
    trackCMSElementClicked({
      content: 'redeem',
      type: 'button',
    })
    if (!coupon) {
      setError('coupon', { message: 'Please enter a coupon code.' })
      return
    }
    try {
      await queryClient.query(couponCodeQueryOptions({ gift_code: coupon }))
      router.push(`${signupUrl}&gift_code=${coupon}`)
    } catch (e) {
      setError('coupon', { message: getErrorMessage(e) })
    }
  }
  try {
    return (
      <form {...getCMSWrapperProps({ content_type_id, id })} onSubmit={handleSubmit(onSubmit)}>
        <CMSFormInputWrapper spacing="md" justify="center">
          <CMSInputWrapper>
            <TextInput
              type="text"
              aria-label="Coupon code"
              placeholder="Coupon code"
              errorMessage={errors.coupon?.message}
              isClearable
              {...register('coupon')}
            />
          </CMSInputWrapper>
          <CMSFormSubmitButton type="submit">Redeem</CMSFormSubmitButton>
        </CMSFormInputWrapper>
        {!!disclaimer && <Body size="sm">{disclaimer}</Body>}
      </form>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}
